import {
  Activity,
  ArrowRightLeft,
  BookOpen,
  Briefcase,
  CalendarDays,
  Car,
  Clock,
  Columns3,
  Database,
  Files,
  GanttChart,
  Gauge,
  Globe,
  IdCard,
  LayoutDashboard,
  ListChecks,
  type LucideIcon,
  Mail,
  MessageSquare,
  MessageSquarePlus,
  PiggyBank,
  Receipt,
  Settings as SettingsIcon,
  UsersRound,
  Workflow as WorkflowIcon,
} from 'lucide-react'

export type NavKey =
  | 'dashboard_overview'
  | 'chat'
  | 'mail'
  | 'calendar'
  | 'team_planning'
  | 'timeAccounts'
  | 'projects'
  | 'logbook'
  | 'documents'
  | 'disposition'
  | 'invoices'
  | 'finanzplanung'
  | 'coaching_abos'
  | 'workflows'
  | 'tasks'
  | 'tickets'
  | 'kanban'
  | 'activity_log'
  | 'personnel'
  | 'stammdaten'
  | 'settings'
  | 'beta_website'
  | 'guide'
  | 'import_export'

export interface NavItemDef {
  key: NavKey
  label: string
  icon: LucideIcon
  to: string
  requiresAdmin?: boolean
}

export interface NavGroup {
  id: string
  label?: string
  items: NavItemDef[]
}

export const NAV_GROUPS: NavGroup[] = [
  {
    id: 'overview',
    items: [
      { key: 'dashboard_overview', label: 'Dashboard', icon: LayoutDashboard, to: '/' },
      { key: 'chat', label: 'Chat', icon: MessageSquare, to: '/chat' },
      { key: 'mail', label: 'Mail', icon: Mail, to: '/mail' },
    ],
  },
  {
    id: 'time',
    label: 'Zeiterfassung',
    items: [
      { key: 'calendar', label: 'Kalender', icon: CalendarDays, to: '/calendar' },
      { key: 'timeAccounts', label: 'Stundenkonten', icon: Clock, to: '/time-accounts' },
      { key: 'team_planning', label: 'Teamplanung', icon: UsersRound, to: '/team' },
    ],
  },
  {
    id: 'management',
    label: 'Management',
    items: [
      { key: 'projects', label: 'Projekte', icon: Briefcase, to: '/projects' },
      { key: 'logbook', label: 'Fahrtenbücher', icon: Car, to: '/logbook' },
      { key: 'documents', label: 'Dateien', icon: Files, to: '/documents' },
      { key: 'stammdaten', label: 'Stammdaten', icon: Database, to: '/stammdaten' },
      {
        key: 'disposition',
        label: 'Disposition',
        icon: GanttChart,
        to: '/disposition',
        requiresAdmin: true,
      },
      { key: 'invoices', label: 'Rechnungen', icon: Receipt, to: '/invoices' },
      { key: 'finanzplanung', label: 'Finanzplanung', icon: PiggyBank, to: '/finanzplanung' },
      {
        key: 'coaching_abos',
        label: 'Coaching-Abos',
        icon: Gauge,
        to: '/coaching/abos',
        requiresAdmin: true,
      },
      { key: 'workflows', label: 'Workflows', icon: WorkflowIcon, to: '/workflows' },
    ],
  },
  {
    id: 'feedback',
    label: 'Feedback',
    items: [
      { key: 'tasks', label: 'Aufgaben', icon: ListChecks, to: '/tasks' },
      { key: 'tickets', label: 'Tickets', icon: MessageSquarePlus, to: '/tickets' },
      { key: 'kanban', label: 'Board', icon: Columns3, to: '/kanban', requiresAdmin: true },
    ],
  },
  {
    id: 'admin',
    label: 'Verwaltung',
    items: [
      { key: 'activity_log', label: 'Verlauf', icon: Activity, to: '/activity-log' },
      { key: 'personnel', label: 'Personalverwaltung', icon: IdCard, to: '/settings/teams' },
      { key: 'settings', label: 'Einstellungen', icon: SettingsIcon, to: '/settings/profile' },
      { key: 'beta_website', label: 'Beta-Website', icon: Globe, to: '/welcome' },
      { key: 'guide', label: 'Guide', icon: BookOpen, to: '/guide' },
      {
        key: 'import_export',
        label: 'Import / Export',
        icon: ArrowRightLeft,
        to: '/import-export',
        requiresAdmin: true,
      },
    ],
  },
]

// Default visibility per role. Anything not listed is visible to admin only.
const ROLE_DEFAULTS: Record<'admin' | 'user', Record<NavKey, boolean>> = {
  admin: {
    dashboard_overview: true,
    chat: true,
    mail: true,
    calendar: true,
    team_planning: true,
    timeAccounts: true,
    projects: true,
    logbook: true,
    documents: true,
    disposition: true,
    invoices: true,
    finanzplanung: true,
    coaching_abos: true,
    workflows: true,
    tasks: true,
    tickets: true,
    kanban: true,
    activity_log: true,
    personnel: true,
    stammdaten: true,
    settings: true,
    beta_website: true,
    guide: true,
    import_export: true,
  },
  user: {
    dashboard_overview: true,
    chat: true,
    mail: true,
    calendar: true,
    team_planning: true,
    timeAccounts: true,
    projects: true,
    logbook: true,
    documents: true,
    disposition: false,
    invoices: false,
    // Persönliche Finanzplanung — bewusst für alle Mitarbeiter sichtbar
    // (Selbst-Service); per Rollen-sidebarVisibility ausblendbar.
    finanzplanung: true,
    coaching_abos: false,
    workflows: false,
    tasks: true,
    tickets: true,
    kanban: false,
    // Personalverwaltung is admin-only-ish: regulars can see /settings/teams
    // already; gate at the route-level if you want to hide the entry entirely.
    personnel: true,
    // Verlauf shows own actions for regular users (server-side RBAC handles
    // the "team-leader sees own + team" expansion automatically); leave on.
    activity_log: true,
    stammdaten: true,
    settings: true,
    beta_website: true,
    guide: true,
    import_export: false,
  },
}

export function isItemVisible(
  item: NavItemDef,
  user: {
    role: 'admin' | 'user'
    roleObj?: { isAdmin: boolean; sidebarVisibility?: Record<string, boolean> | null } | null
  } | null,
): boolean {
  if (!user) return false
  const isAdmin = user.role === 'admin' || (user.roleObj?.isAdmin ?? false)
  if (item.requiresAdmin && !isAdmin) return false
  // Sichtbarkeit kommt jetzt nur noch von der Rolle (custom oder statisch).
  // Ältere per-User-Overrides werden bewusst ignoriert — die wären sonst
  // der Grund, warum eine Rollen-Änderung beim betroffenen User nicht
  // greift. Per-User-Konfiguration ist aus der UI entfernt.
  const roleVis = user.roleObj?.sidebarVisibility
  if (roleVis) {
    const roleOverride = roleVis[item.key]
    if (roleOverride === false) return false
    if (roleOverride === true) return true
  }
  // Fallback auf statische Defaults (admin / user).
  const staticRole = isAdmin ? 'admin' : 'user'
  return ROLE_DEFAULTS[staticRole]?.[item.key] ?? true
}

export function visibleGroups(
  user: {
    role: 'admin' | 'user'
    roleObj?: { isAdmin: boolean; sidebarVisibility?: Record<string, boolean> | null } | null
  } | null,
): NavGroup[] {
  return NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((it) => isItemVisible(it, user)),
  })).filter((g) => g.items.length > 0)
}

export function defaultVisibilityFor(role: 'admin' | 'user'): Record<NavKey, boolean> {
  return { ...ROLE_DEFAULTS[role] }
}
